// =============================================================
//  keyLayers.js — how a key finds its way, on any Oscilla page
//
//  The mechanism; the keys themselves are the app's. The score's
//  global keymap is keyboardShortcuts.js, which hands it over with
//  installKeyHandling(); Oscilla Live (live.js) has none, and its
//  console keys are its own.
//
//  ── Three stages, in order ───────────────────────────────────
//   1. Escape      unwinds one layer of the Escape stack (below).
//   2. Contexts    an active tool gets first refusal on a key, so
//                  drawing owns D/E/V/S while it is running.
//   3. Global map  whatever no context claimed.
//  Ahead of all three, a keyboard owner (the live console in perform
//  mode) can take every key.
//
//  ── The Escape stack ─────────────────────────────────────────
//  Oscilla has many toggles that capture pointer input or cover the
//  score. Escape backs out of exactly ONE per press — the innermost
//  active layer — so a stack of states unwinds predictably instead
//  of everything collapsing at once.
//
//  What belongs on the stack: anything that captures input or overlays
//  the score. What does not: persistent *settings* such as freeze
//  animations, mic analysis, marker or playhead visibility. Escape
//  should never silently flip a setting you deliberately switched on.
//
//  Innermost first:
//   overlay    fullscreen cue video
//   popup      shortcut help overlay · rehearsal mark chooser
//   substate   DSL path-pick · preset click-to-place · group wrap panel
//   selection  group selection · audio-object and annotation selection
//   panel      DSL editor sidebar · preset sidebar · live console
//   mode       group select · DSL inspector · draw/erase/select ·
//              annotation edit · audio-object edit · notation create/edit
//
//  Layers of equal priority are dismissed in registration order.
//  Most same-tier modes are mutually exclusive anyway, so this only
//  decides cases such as group select and the inspector both being on.
//
//  ── Interop with local Escape handlers ───────────────────────
//  The listener runs at window capture, i.e. before every other
//  handler, but it only consumes a key it actually acts on. Anything
//  else falls through untouched to the handlers that own it. It also
//  always defers while focus is in a text field.
// =============================================================

// -------------------------------------------------------------
//  Priorities — higher is dismissed first (innermost).
// -------------------------------------------------------------
export const ESC_PRIORITY = {
    OVERLAY:   100,  // fullscreen media covering the score
    POPUP:      90,  // transient chooser opened on top of everything else
    SUBSTATE:   80,  // pick / place / wrap-config armed inside a mode
    SELECTION:  70,  // a selection made inside a mode
    PANEL:      60,  // sidebars and consoles
    EDIT_MODE:  40,  // modes that capture clicks on the score
};

// Tier names modules may use instead of a raw number.
const TIER = {
    overlay:   ESC_PRIORITY.OVERLAY,
    popup:     ESC_PRIORITY.POPUP,
    substate:  ESC_PRIORITY.SUBSTATE,
    selection: ESC_PRIORITY.SELECTION,
    panel:     ESC_PRIORITY.PANEL,
    mode:      ESC_PRIORITY.EDIT_MODE,
};

/** @type {{id: string, priority: number, isActive: () => boolean, dismiss: () => void}[]} */
const _layers = [];
let _installed = false;
let _runGlobal = () => false;

/**
 * Register a dismissible layer with the Escape stack.
 *
 * Give either `tier` (a key of TIER: overlay | substate | selection | panel |
 * mode) or an explicit numeric `priority`. Re-registering an id replaces it.
 *
 * @param {object}        layer
 * @param {string}        layer.id        stable id, used for logging and replacement
 * @param {string}        [layer.tier]    tier name; mapped to a priority
 * @param {number}        [layer.priority] explicit priority, higher is dismissed first
 * @param {() => boolean} layer.isActive  true when this layer can be dismissed
 * @param {() => void}    layer.dismiss   back out of exactly this layer
 */
export function registerEscapeLayer({ id, tier, priority, isActive, dismiss }) {
    const p = priority ?? TIER[tier];
    if (p == null) {
        console.warn(`[shortcuts] layer "${id}" has no tier or priority — ignored`);
        return;
    }
    const layer = { id, priority: p, isActive, dismiss };
    const existing = _layers.findIndex(l => l.id === id);
    if (existing >= 0) _layers[existing] = layer;
    else _layers.push(layer);
}

// -------------------------------------------------------------
//  Typing guard
// -------------------------------------------------------------

/**
 * True while the user is typing, so shortcuts must keep their hands off.
 * Honours window.oscillaTextInputActive, which modules set for editors
 * that are not a plain input element.
 */
export function isTypingInInput() {
    if (window.oscillaTextInputActive) return true;
    const el = document.activeElement;
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

// Escape is the exception: a text field handles it locally to cancel the
// edit, so the stack defers on the focused element rather than the flag.
function _isTextEntry(el) {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

// -------------------------------------------------------------
//  Key contexts — an active tool gets first refusal on a key
// -------------------------------------------------------------

/** @type {{id: string, priority: number, isActive: () => boolean, handle: (e) => boolean}[]} */
const _contexts = [];

/**
 * Register a context that claims keys while it is active.
 *
 * `handle` returns true if it consumed the key. Contexts are tried
 * highest priority first, and the first to consume wins; anything left
 * over falls through to the global map. This is what lets drawing own
 * bare D/E/V/S without those letters being lost app-wide.
 *
 * @param {object}        ctx
 * @param {string}        ctx.id
 * @param {number}        [ctx.priority]  higher is asked first, default 0
 * @param {() => boolean} ctx.isActive
 * @param {(e: KeyboardEvent) => boolean} ctx.handle
 */
export function registerKeyContext({ id, priority = 0, isActive, handle }) {
    const ctx = { id, priority, isActive, handle };
    const at = _contexts.findIndex(c => c.id === id);
    if (at >= 0) _contexts[at] = ctx;
    else _contexts.push(ctx);
}

/** Drop a previously registered context (module teardown). */
export function unregisterKeyContext(id) {
    const at = _contexts.findIndex(c => c.id === id);
    if (at >= 0) _contexts.splice(at, 1);
}

// -------------------------------------------------------------
//  The listener
// -------------------------------------------------------------

// Overlays owned by their own Escape handlers. Listed rather than
// registered because the state lives deep inside those modules and
// their handlers already do the right thing; the stack just steps
// aside so they still see the key.
function _foreignModalOpen() {
    if (window.__stopwatchFullscreen) return true;                  // timers.js
    if (document.querySelector('dialog[open]')) return true;
    return false;
}

// Something that, while it is on, owns the keyboard outright: the live
// console in perform mode. It sees every key first -- this listener is on
// window in the capture phase, ahead of everything else -- and returns
// true for one it took, which then goes nowhere else.
let _keyboardOwner = null;
export function setKeyboardOwner(fn) { _keyboardOwner = fn || null; }

function _onKeyDown(e) {
    if (_keyboardOwner?.(e)) return;
    if (e.defaultPrevented) return;
    if (e.key === 'Escape') { _onEscape(e); return; }

    // Everything past here is a plain, unmodified key.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (isTypingInInput()) return;

    // 1. an active tool gets first refusal
    for (const ctx of [..._contexts].sort((a, b) => b.priority - a.priority)) {
        let active = false;
        try { active = !!ctx.isActive(); }
        catch (err) { console.warn(`[shortcuts] context "${ctx.id}" isActive threw`, err); }
        if (!active) continue;
        let consumed = false;
        try { consumed = !!ctx.handle(e); }
        catch (err) { console.warn(`[shortcuts] context "${ctx.id}" handle threw`, err); }
        if (consumed) { e.preventDefault(); e.stopPropagation(); return; }
    }

    // 2. whatever no context claimed: the app's keymap
    if (_runGlobal(e)) { e.preventDefault(); e.stopPropagation(); }
}

function _onEscape(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (_isTextEntry(e.target)) return;
    if (_foreignModalOpen()) return;

    let top = null;
    for (const layer of _layers) {
        let active = false;
        try { active = !!layer.isActive(); }
        catch (err) { console.warn(`[shortcuts] layer "${layer.id}" isActive threw`, err); }
        if (!active) continue;
        if (!top || layer.priority > top.priority) top = layer;
    }
    if (!top) return;   // nothing of ours is open — leave Escape to whoever owns it

    e.preventDefault();
    e.stopPropagation();
    try { top.dismiss(); }
    catch (err) { console.warn(`[shortcuts] layer "${top.id}" dismiss threw`, err); }
}

// -------------------------------------------------------------
//  Install
// -------------------------------------------------------------

/**
 * Start listening. Call once; `runGlobal(e)` is the app's keymap, the
 * last stage, and returns true for a key it took.
 *
 * @param {(e: KeyboardEvent) => boolean} [runGlobal]
 */
export function installKeyHandling(runGlobal) {
    if (_installed) return;
    _installed = true;
    if (runGlobal) _runGlobal = runGlobal;
    window.addEventListener('keydown', _onKeyDown, true);
}
